import clsx from "clsx";
import {
  ClipboardEvent,
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ErrorMessage } from "@/components/Interface/FormFields/ErrorMessage/ErrorMessage";

const variantClassMapping = {
  light: {
    label: "text-foreground-primary",
    container: "text-foreground-primary",
    input: "border-border-secondary text-foreground-primary",
    // Bordered/filled rounded box (light) instead of an underline.
    box: "h-12 w-10 rounded-md border-2 bg-surface-tertiary focus:border-border-primary",
  },
  dark: {
    label: "text-foreground-primary-inverse",
    container: "text-foreground-primary-inverse",
    input: "border-foreground-primary-inverse text-foreground-primary-inverse",
    // Bordered/filled rounded box (dark dialog) instead of an underline.
    box: "h-12 w-10 rounded-md border-2 bg-surface-tertiary-inverse focus:bg-surface-secondary-inverse",
  },
};

export type TwoFactorConfirmationCodeForm = {
  code: string;
};

export const TwoFactorConfirmationInput = ({
  error,
  clearError,
  variant = "light",
}: {
  error?: string;
  clearError?: () => void;
  variant?: "light" | "dark";
}) => {
  const { t } = useTranslation();
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const {
    setValue,
    formState: { errors },
    clearErrors,
    control,
  } = useFormContext<TwoFactorConfirmationCodeForm>();

  const hasError = !!errors.code || !!error;
  const code = useWatch({ control, name: "code" });

  // When code is reset to "", clear DOM input values
  useEffect(() => {
    if (code === "") {
      inputRefs.current.forEach((input) => (input.value = ""));
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }
  }, [code]);

  // On backspace keydown, we want to delete to the previous input field
  // if the field is already empty.
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && (e.target as HTMLInputElement).value === "") {
      const prevInput = inputRefs.current[Math.max(0, index - 1)];
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  // On input, we want to move to the next input field if we get a character
  // and the field is not already holding a value.
  const handleInput = useCallback(
    (e: FormEvent<HTMLInputElement>, index: number) => {
      const inputElement = e.target as HTMLInputElement;
      const [firstCharacter, ...remainingInput] = inputElement.value;

      if (firstCharacter === undefined) {
        return;
      }

      inputElement.value = firstCharacter ?? "";
      setValue("code", inputRefs.current.map((input) => input.value).join(""));
      clearErrors("code");

      if (clearError) {
        clearError();
      }
      if (firstCharacter !== undefined) {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
          nextInput.value = remainingInput.join("");
          nextInput.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
    },
    [setValue, clearErrors, clearError],
  );

  // On paste, we want to paste the first 6 characters into the input fields
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasteData = e.clipboardData?.getData("text").slice(0, 6) ?? "";
    inputRefs.current.forEach((input, i) => {
      input.value = pasteData[i] || "";
      input.dispatchEvent(new Event("input", { bubbles: true }));
    });
  };

  const variantClasses = variantClassMapping[variant];

  return (
    <fieldset>
      <label
        className={clsx(
          "flex flex-col gap-2 text-lg font-semibold",
          variantClasses.label,
        )}
      >
        <span>{t("portal:authenticator.sms.verification.label")}</span>
        <div
          className={clsx(
            "flex w-fit justify-center gap-4",
            variantClasses.container,
          )}
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <input
              key={index}
              className={clsx(
                "text-center text-lg focus:outline-none",
                variantClasses.box,
                hasError && "border-border-error text-foreground-error",
                !hasError && variantClasses.input,
              )}
              required
              maxLength={1}
              inputMode="numeric"
              pattern="[0-9]*"
              onKeyDown={(e) => handleKeyDown(e, index)}
              onInput={(e) => handleInput(e, index)}
              onPaste={handlePaste}
              ref={(el) => {
                if (el) {
                  inputRefs.current[index] = el;
                }
              }}
            />
          ))}
        </div>
      </label>
      {hasError && (
        <ErrorMessage name="code" message={error || errors.code?.message} />
      )}
    </fieldset>
  );
};
